import { z } from "zod";
/**
 * Task 9.8 — a cellar changing hands.
 *
 * **This is the invitation contract's opposite, deliberately.** An invitation
 * names an address that may have no account behind it, because there is
 * nothing for the invited person to do: read access cannot be lost by being
 * given, so it is granted rather than asked for. A transfer is nothing *but*
 * the other person's decision — it changes what somebody owns — so it is
 * offered to an account, and it happens only when that account says yes.
 *
 * Two sides, and they do not see the same thing. The owner sees the offer they
 * made and the cellar's history of changing hands; the person offered a cellar
 * sees an offer with the cellar's name and who is handing it over, on the list
 * of cellars, because it is the one screen they cannot help meeting.
 */
/**
 * The same address rule the invitation uses, folded to lower case for the same
 * reason: an address is an identity here, and `Erik@` and `erik@` are one
 * person. Written out rather than imported, because the two contracts are
 * about different acts and one changing is not a reason for the other to.
 */
const emailAddress = z.string().trim().toLowerCase().email();
/**
 * How an offer ended, or nothing while it is still waiting.
 *
 * Kept after the fact rather than deleted: a cellar that changed hands is a
 * fact about the cellar, and the two endings that changed nothing still say
 * what was tried.
 */
export const transferOutcomeSchema = z.enum(["Accepted", "Declined", "Withdrawn"]);
/**
 * An offer as the owner's page shows it: the person it went to, by name, and
 * when. The address comes with the name for the reason the guest list gives —
 * two Eriks are told apart by where they are written to.
 */
export const cellarTransferSchema = z.object({
    id: z.string(),
    toName: z.string(),
    toEmail: z.string(),
    fromName: z.string(),
    offeredAt: z.string(),
    settledAt: z.string().nullable(),
    outcome: transferOutcomeSchema.nullable(),
});
/**
 * What the owner's page is: the one offer that may be waiting, and everything
 * that has already happened to this cellar.
 *
 * `pending` is separate from `history` rather than being its first row,
 * because the two are different in kind — one is a thing that can still be
 * taken back, and the rest is the record. A screen that had to look for a null
 * outcome to know which was which would be re-deciding that on every render.
 */
export const cellarTransfersResponseSchema = z.object({
    pending: cellarTransferSchema.nullable(),
    // Required as of `10.7`. `pending` beside it never carried a default, and
    // the two are sent by the same line of the same route: one of them being
    // lenient was an accident of the order they were written in, not a rule.
    history: z.array(cellarTransferSchema),
});
export const offerTransferRequestSchema = z.object({ email: emailAddress });
/**
 * An offer as the person receiving it sees it, on the list of cellars.
 *
 * It carries the cellar's name and the name of the person handing it over,
 * which is the whole of what somebody needs in order to know what is being
 * asked. Not the contents: a cellar that has not been accepted is not one this
 * account may read, and a count of its bottles would be the first thing it
 * told them about a cellar they may yet decline.
 */
export const transferOfferSchema = z.object({
    id: z.string(),
    cellarName: z.string(),
    fromName: z.string(),
    fromEmail: z.string(),
    offeredAt: z.string(),
});
//# sourceMappingURL=transfer.js.map